import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";

const clampText = (text = "", max = 170) => {
    const t = String(text).replace(/\s+/g, " ").trim();
    if (!t) return "";
    if (t.length <= max) return t;
    const cut = t.slice(0, max);
    const lastSpace = cut.lastIndexOf(" ");
    return `${cut.slice(0, Math.max(0, lastSpace)).trim()}…`;
};

const THEME = {
    franklynews: { overlay: "/img/article_maroon.png", border: "#6d1b1b" },
    habbonews: { overlay: "/img/article_gold.png", border: "#c48a00" },
    chickacharity: { overlay: "/img/article_pink.png", border: "#c86aa0" },
    values: { overlay: "/img/article_lightblue.png", border: "#4f9ea4" },
    guides: { overlay: "/img/article_green.png", border: "#2d6a1e" },
    competition: { overlay: "/img/article_purple.png", border: "#6c2d82" }
};

// Spotlight colour mapping
const SPOTLIGHT_COLORS = {
    1: { overlay: "/img/article_gold.png", border: "#c48a00" },           // Gold
    2: { overlay: "/img/article_green.png", border: "#2d6a1e" },          // Green
    3: { overlay: "/img/article_lightblue.png", border: "#4f9ea4" },      // Light Blue
    4: { overlay: "/img/article_maroon.png", border: "#6d1b1b" },         // Maroon
    5: { overlay: "/img/article_pink.png", border: "#c86aa0" },           // Pink
    6: { overlay: "/img/article_purple.png", border: "#6c2d82" }          // Purple
};

export default function ArticleCarousel({
                                            articles = [],
                                            intervalMs = 4500,
                                            backgroundTile = "/img/grey_tile.png"
                                        }) {
    const sorted = useMemo(() => {
        return [...articles]
            .filter((a) => a && a.id)
            .sort((a, b) => (a.sortOrder ?? 9999) - (b.sortOrder ?? 9999))
            .slice(0, 10)
            .map((a) => {
                const bodyText = a.body || "";
                const overview =
                    (a.overview && String(a.overview).trim()) ||
                    clampText(bodyText, 175) ||
                    "Placeholder overview text for this article teaser. This will be replaced with real data.";

                // Generate article URL with DDMM format
                const date = new Date(a.publicDate);
                const day = String(date.getUTCDate()).padStart(2, '0');
                const month = String(date.getUTCMonth() + 1).padStart(2, '0');
                const dateSlug = day + month;
                const titleSlug = a.title
                    .toLowerCase()
                    .replace(/[^\w\s-]/g, '')
                    .replace(/\s+/g, '-')
                    .replace(/-+/g, '-');

                return {
                    id: a.id,
                    title: a.title || "Placeholder article title",
                    overview,
                    theme: a.theme || "habbonews",
                    spotlightColorId: a.spotlightColorId,
                    image: a.headerImage || "",
                    href: a.href || `/article/${dateSlug}/${titleSlug}`
                };
            });
    }, [articles]);

    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const timerRef = useRef(null);

    useEffect(() => {
        if (sorted.length <= 1) return;
        if (paused) return;

        timerRef.current = setInterval(() => {
            setIndex((i) => (i + 1) % sorted.length);
        }, intervalMs);

        return () => {
            if (timerRef.current) clearInterval(timerRef.current);
            timerRef.current = null;
        };
    }, [sorted.length, paused, intervalMs]);

    useEffect(() => {
        if (index >= sorted.length) setIndex(0);
    }, [index, sorted.length]);

    if (!sorted.length) {
        return (
            <div className="w-full min-h-[300px] bg-offwhite mb-[16px] overflow-hidden border border-black/10" />
        );
    }

    const currentArticle = sorted[index] ?? sorted[0];
    if (!currentArticle) return null;
    const activeTheme = currentArticle.spotlightColorId
        ? SPOTLIGHT_COLORS[currentArticle.spotlightColorId] || THEME.habbonews
        : THEME[currentArticle.theme] || THEME.habbonews;

    return (
        <div
            className="w-full min-h-[260px] mb-[16px] relative overflow-hidden border-[3px]"
            style={{ borderColor: activeTheme.border }}
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
        >
            <div className="absolute inset-0">
                {sorted.map((a, i) => {
                    const active = i === index;
                    const theme = a.spotlightColorId
                        ? SPOTLIGHT_COLORS[a.spotlightColorId] || THEME.habbonews
                        : THEME[a.theme] || THEME.habbonews;

                    return (
                        <div
                            key={a.id}
                            className={[
                                "absolute inset-0 transition-opacity duration-300 ease-linear",
                                active ? "opacity-100" : "opacity-0 pointer-events-none"
                            ].join(" ")}
                        >
                            <div
                                className="absolute inset-0"
                                style={{
                                    backgroundImage: `url(${backgroundTile})`,
                                    backgroundRepeat: "repeat",
                                    backgroundPosition: "right center",
                                    backgroundSize: "auto"
                                }}
                            />

                            {a.image && (
                                <img
                                    src={a.image}
                                    alt=""
                                    draggable={false}
                                    className="
                                        absolute
                                        right-[-180px] sm:right-[-10px]
                                        top-1/2 -translate-y-1/2
                                        h-[102%] max-w-none w-auto object-contain
                                        opacity-80 pointer-events-none select-none
                                      "
                                    style={{
                                        WebkitMaskImage:
                                            "linear-gradient(to right, rgba(0,0,0,0) 0px, rgba(0,0,0,1) 50px, rgba(0,0,0,1) 100%)",
                                        maskImage:
                                            "linear-gradient(to right, rgba(0,0,0,0) 0px, rgba(0,0,0,1) 50px, rgba(0,0,0,1) 100%)",
                                        WebkitMaskRepeat: "no-repeat",
                                        maskRepeat: "no-repeat",
                                        WebkitMaskSize: "100% 100%",
                                        maskSize: "100% 100%"
                                    }}
                                />
                            )}

                            <div className="absolute inset-0 bg-black/15" />

                            <div
                                className="absolute inset-0 bg-no-repeat bg-[position:calc(100%+220px)_center] sm:bg-[position:calc(100%+20px)_center]"
                                style={{
                                    backgroundImage: `url(${theme.overlay})`,
                                    backgroundSize: "auto"
                                }}
                            />

                            <div className="absolute inset-0 bg-gradient-to-t from-[#1a1a1a]/85 via-[#1a1a1a]/40 to-transparent" />

                            <div className="relative h-full w-full p-4 sm:p-6 flex flex-col justify-end">
                                <div className="w-[calc(100%-88px)] sm:w-[calc(100%-150px)] lg:w-[70%] max-w-[760px]">
                                    <Link
                                        to={a.href}
                                        className="font-['Jersey_10'] text-[26px] sm:text-[34px] leading-none text-offwhite drop-shadow-[0_1px_0_rgba(255,255,255,0.35)] hover:underline w-fit"
                                    >
                                        {a.title}
                                    </Link>

                                    <Link
                                        to={a.href}
                                        className="mt-2 block text-[13px] sm:text-[14px] leading-snug text-offwhite/90 max-w-[52ch] hover:underline"
                                    >
                                        <span className="block sm:hidden">{clampText(a.overview, 25)}</span>
                                        <span className="hidden sm:block">{clampText(a.overview, 75)}</span>
                                    </Link>
                                </div>

                                <div className="mt-4 pb-2 flex items-center justify-center gap-2">
                                    {sorted.map((_, dotIdx) => {
                                        const dotActive = dotIdx === index;
                                        return (
                                            <button
                                                key={`${a.id}-dot-${dotIdx}`}
                                                type="button"
                                                aria-label={`Go to article ${dotIdx + 1}`}
                                                onClick={() => setIndex(dotIdx)}
                                                className={[
                                                    "h-[10px] w-[10px] rounded-full border transition-all",
                                                    dotActive
                                                        ? "bg-black border-black"
                                                        : "bg-white/70 border-black/40 hover:bg-white"
                                                ].join(" ")}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}